import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, featuredArticles, latestArticles } from '@/mocks/data';

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功！感谢您的关注。');
      setEmail('');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pmh09n2trhb35gvhug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('订阅成功！感谢您的关注，我们将定期为您推送最新内容。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setStatus('error');
          setMessage('提交失败，请稍后重试。');
        } else {
          setStatus('error');
          setMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setStatus('error');
      setMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        name="website_alt"
        className="bot-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <input
        type="email"
        name="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="请输入您的邮箱地址"
        required
        className="flex-1 px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
      />
      <button
        type="submit"
        className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
      >
        立即订阅
      </button>
      {status !== 'idle' && (
        <p className={`text-sm mt-2 sm:mt-0 sm:absolute sm:-bottom-7 sm:left-0 ${status === 'success' ? 'text-secondary-600' : 'text-red-500'}`}>
          {message}
        </p>
      )}
    </form>
  );
}

const faqData = [
  {
    q: '91天堂的资源都是正版吗？',
    a: '是的，91天堂平台上所有内容均为正版授权资源。我们与多家内容提供商建立了长期合作关系，确保每一部作品都经过合法授权，用户可以放心观看。',
  },
  {
    q: '如何注册成为91天堂会员？',
    a: '点击页面右上角的「注册会员」按钮，填写基本信息即可完成注册。注册后立即可享受高清画质、去广告体验等专属会员权益。',
  },
  {
    q: '平台支持哪些设备访问？',
    a: '91天堂支持PC端浏览器、手机浏览器以及平板设备访问，所有页面均采用响应式设计，在不同设备上都能获得良好的浏览体验。',
  },
  {
    q: '内容更新频率是怎样的？',
    a: '91天堂坚持每日更新，电影、电视剧、综艺、动漫等各分类均有稳定的更新节奏。热门内容通常在首播后24小时内上线，确保用户能第一时间获取最新资源。',
  },
  {
    q: '如果遇到播放问题怎么办？',
    a: '如遇到播放卡顿或加载问题，建议先检查网络连接状态。如果问题持续存在，可通过关于我们页面的联系方式联系客服团队，我们会在24小时内为您解决。',
  },
];

const membershipBenefits = [
  { icon: 'ri-4k-line', title: '超清画质', desc: '支持4K超高清画质播放，享受影院级视觉体验' },
  { icon: 'ri-speed-up-line', title: '极速加载', desc: '全球CDN加速节点，秒级加载不卡顿' },
  { icon: 'ri-shield-check-line', title: '正版保障', desc: '全部内容正版授权，安全稳定可靠' },
  { icon: 'ri-refresh-line', title: '每日更新', desc: '热门内容每日同步更新，内容永不掉队' },
  { icon: 'ri-advertisement-line', title: '无广告体验', desc: '会员尊享纯净观影体验，专注于内容本身' },
  { icon: 'ri-vip-crown-line', title: '专属权益', desc: '会员专属内容与活动，福利享不停' },
];

export default function Home() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const toggleFaq = (idx: number) => {
    setFaqOpen(faqOpen === idx ? null : idx);
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20dark%20cinematic%20background%20with%20flowing%20gradient%20waves%20in%20warm%20amber%20and%20deep%20charcoal%20tones%2C%20soft%20particle%20effects%2C%20elegant%20light%20streaks%2C%20luxury%20entertainment%20atmosphere%2C%20minimal%20modern%20design%20with%20smooth%20curves%20and%20geometric%20accents&width=1920&height=1080&seq=hero-bg-main&orientation=landscape')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              91天堂官方入口
              <br />
              <span className="text-primary-300">正版资源全覆盖</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              海量正版资源、极速加载体验与安全稳定的访问环境。清晰画质、丰富分类与每日更新内容，注册即可解锁专属会员权益。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                立即注册
              </Link>
              <Link
                to="/category/dianying"
                className="px-8 py-3.5 rounded-lg bg-white/15 text-white text-sm font-semibold hover:bg-white/25 backdrop-blur-sm transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/20"
              >
                浏览内容
              </Link>
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                内容分类
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                涵盖电影、电视剧、综艺、动漫等多个领域，满足不同用户的多样化需求
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-1.5">
                    {cat.name}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-2">
                    {cat.description}
                  </p>
                  <span className="text-xs text-foreground-400">
                    {cat.articleCount} 部资源
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500">编辑团队精心挑选的高质量内容</p>
              </div>
              <Link
                to="/category/dianying"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-xl bg-background-50 border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-background-900/70 text-background-50 text-xs font-medium backdrop-blur-sm">
                        {categories.find((c) => c.id === article.categoryId)?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500">每日更新，精彩内容不间断</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-1.5 group-hover:text-primary-500 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                        {categories.find((c) => c.id === article.categoryId)?.name}
                      </span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Benefits */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                会员专属权益
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                注册成为91天堂会员，解锁更多精彩内容和专属功能
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {membershipBenefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-xl bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 mb-4">
                    <i className={`${benefit.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {benefit.desc}
                  </p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/about"
                className="inline-flex px-8 py-3.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                立即注册，解锁全部权益
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                关于91天堂的常见疑问解答，帮助您更好地了解我们的平台
              </p>
            </div>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqData.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-background-100 border border-background-200 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    onClick={() => toggleFaq(idx)}
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">
                      {item.q}
                    </span>
                    <i
                      className={`text-base text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                        faqOpen === idx ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {faqOpen === idx && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-16 md:py-20 bg-background-900">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
              订阅最新资讯
            </h2>
            <p className="text-sm md:text-base text-background-400 max-w-xl mx-auto mb-8">
              订阅我们的资讯邮件，第一时间获取最新内容更新和会员专属活动信息
            </p>
            <div className="max-w-lg mx-auto relative">
              <NewsletterForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '91天堂',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '91天堂是深受用户喜爱的内容平台，提供海量正版资源、极速加载体验与安全稳定的访问环境。',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: typeof window !== 'undefined' ? `${window.location.origin}/category/dianying` : '',
              },
              'query-input': 'required name=search_term',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '91天堂',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '91天堂是深受用户喜爱的内容平台，提供海量正版资源、极速加载体验与安全稳定的访问环境。',
            sameAs: [],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '91天堂 - 首页',
            description: '91天堂官方入口，正版资源全覆盖。海量高清电影、电视剧、综艺、动漫等丰富内容，每日更新，极速加载。',
            url: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}